import { useCallback } from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { useMemo } from 'react';
import { useCustomVizAutoload } from 'apps/sigma2-ui/src/lib/hooks/useCustomVizAutoload';
import { CustomVizHook } from 'apps/sigma2-ui/src/components/custom-viz/hooks/custom-viz-hook';
import { useShareLinkRequest } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useShareLinkSearchParameter } from 'apps/sigma2-ui/src/lib/hooks/useShareLinkRequest';
import { useUser } from 'apps/sigma2-ui/src/state/user';
import { z } from 'zod';
import { S2SafeParsable } from 'libs/end-to-end/src/interfaces';
import { Nullable } from 'libs/end-to-end/src/ui-types';
import { OnUserAnalysisCancelCallback } from 'apps/sigma2-ui/src/lib/hooks/useOnUserAnalysisCancelCallback';
import { UseOnUserAnalysisCancelCallbackParams } from 'apps/sigma2-ui/src/lib/hooks/useOnUserAnalysisCancelCallback';
import { useOnUserAnalysisCancelCallback } from 'apps/sigma2-ui/src/lib/hooks/useOnUserAnalysisCancelCallback';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';

export const useCopilotCustomVizAutoloader = <T extends CustomVizRequest>({
  canAnalyze,
  onAnalyseWithRequest,
  shouldPrefillFilters,
  onAnalyze,
  context,
  setSelections,
  setShouldPrefillFilters,
  lastPersistedRequest,
  type,
  resetFiltersToInitialState,
  resetMetricsToInitialState,
  resetDimensionsToInitialState,
}: {
  canAnalyze: boolean;
  onAnalyseWithRequest: (request: T) => void;
  onAnalyze: () => void;
  context: CustomVizHook;
  lastPersistedRequest: T | null;
  setSelections: (request: Nullable<T>) => void;
  shouldPrefillFilters: boolean;
  setShouldPrefillFilters: (arg0: boolean) => void;
  type: S2SafeParsable<unknown, T>;
  // Omitting onShareLinkLoaded since we're defining that here
} & Omit<UseOnUserAnalysisCancelCallbackParams, 'onShareLinkLoaded'>): {
  shareLinkRequest: CustomVizRequest | null;
  onUserAnalysisCancel: OnUserAnalysisCancelCallback;
} => {
  const { myUser, userAuthenticatedApiCall } = useUser();

  const shareLinkRawData = useShareLinkSearchParameter();

  const [didLoadLastPersistedRequest, setDidLoadLastPersistedRequest] =
    useState(false);

  const canAnalyseInternal = useMemo(() => {
    if (shareLinkRawData !== null) {
      return false;
    }
    return canAnalyze;
  }, [canAnalyze, shareLinkRawData]);

  useCustomVizAutoload({
    canAnalyse: canAnalyseInternal,
    onAnalyze: onAnalyze,
    context,
    ResultZodType: z.object({}),
  });

  useEffect(() => {
    if (shareLinkRawData !== null) {
      return;
    }

    if (lastPersistedRequest) {
      if (!didLoadLastPersistedRequest) {
        setDidLoadLastPersistedRequest(true);
        setSelections(lastPersistedRequest);
      }
    } else if (!shouldPrefillFilters) {
      setShouldPrefillFilters(true);
    }
  }, [
    didLoadLastPersistedRequest,
    lastPersistedRequest,
    onAnalyseWithRequest,
    setSelections,
    setShouldPrefillFilters,
    shareLinkRawData,
    shouldPrefillFilters,
  ]);

  const onShareLinkLoaded = useCallback(
    (request: CustomVizRequest | null) => {
      if (request) {
        const asT = type.safeParse(request);
        if (!asT.success) {
          return;
        }

        const t = asT.data;

        setSelections(t);
        onAnalyseWithRequest(t);
      }
    },
    [onAnalyseWithRequest, setSelections, type],
  );

  const { shareLinkRequest } = useShareLinkRequest({
    myUser,
    userAuthenticatedApiCall,
    type,
    onLoaded: onShareLinkLoaded,
  });

  const onUserAnalysisCancel = useOnUserAnalysisCancelCallback({
    onShareLinkLoaded,
    resetFiltersToInitialState,
    resetMetricsToInitialState,
    resetDimensionsToInitialState,
  });

  return {
    shareLinkRequest,
    onUserAnalysisCancel,
  };
};
